import React, { useState, useRef, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { ChevronDown, ArrowRight } from 'lucide-react';
import { useTaxonomy, slugify } from '../hooks/useTaxonomy';

/**
 * NavFlyouts — desktop hover nav.
 *
 * Renders ONE full-width panel that stays mounted while any nav item is
 * hovered — content swaps based on `openMain`. Sub-components are defined
 * OUTSIDE the parent so React preserves the DOM subtree across hover
 * transitions (no remount → no flicker).
 *
 * A transparent "hover bridge" spans the visual gap between nav bottom
 * and panel top so the cursor never leaves the hover region while
 * travelling down.
 */
const ROOM_MAINS = ['Bedding', 'Living', 'Dining'];
const PANEL_BG = '#EDE3CF';
const PANEL_BORDER = '#C9B596';

// ----- Stable child components (module scope = stable references) -----

const ColumnHeader = ({ to, children, testId, onNavigate }) => (
  <Link
    to={to}
    onClick={onNavigate}
    data-testid={testId}
    className="group/col inline-flex items-center gap-1.5 text-[10px] tracking-[0.32em] uppercase text-[#5B3A24] mb-4 hover:text-[#33200F] transition-colors"
  >
    {children}
    <ArrowRight className="h-3 w-3 transition-transform group-hover/col:translate-x-1" strokeWidth={1.6} />
  </Link>
);

const ColumnItem = ({ to, label, isFooter, testId, onNavigate }) => (
  <li>
    <Link
      to={to}
      onClick={onNavigate}
      data-testid={testId}
      className="group/item inline-block py-[5px] text-[13px] text-[#33200F] hover:text-[#5B3A24] transition-colors"
    >
      <span className={`${isFooter ? 'italic text-[#5B3A24]' : ''} relative`}>
        {label}
        <span className="absolute left-0 right-full bottom-[-2px] h-[1px] bg-[#5B3A24] transition-all duration-300 group-hover/item:right-0" />
      </span>
    </Link>
  </li>
);

const ShopContent = ({ main, sub, styleOpts, allCategories, onNavigate }) => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-16">
    <div>
      <ColumnHeader to="/collections" testId="flyout-shop-by-room" onNavigate={onNavigate}>Shop by Room</ColumnHeader>
      <ul className="space-y-0.5">
        {main.filter((m) => ROOM_MAINS.includes(m)).map((m) => (
          <ColumnItem
            key={m}
            to={`/collections/${slugify(m)}`}
            label={m}
            testId={`flyout-shop-room-${slugify(m)}`}
            onNavigate={onNavigate}
          />
        ))}
      </ul>
    </div>

    <div>
      <ColumnHeader to="/collections/all" testId="flyout-shop-by-category" onNavigate={onNavigate}>Shop by Category</ColumnHeader>
      <ul className="grid grid-cols-2 gap-x-6 gap-y-0.5">
        {allCategories.map(({ main: m, sub: s }) => (
          <ColumnItem
            key={s}
            to={`/collections/${slugify(m)}/${slugify(s)}`}
            label={s}
            testId={`flyout-shop-cat-${slugify(s)}`}
            onNavigate={onNavigate}
          />
        ))}
      </ul>
    </div>

    <div>
      <ColumnHeader to="/collections" testId="flyout-shop-by-material" onNavigate={onNavigate}>Shop by Material</ColumnHeader>
      <ul className="space-y-0.5">
        {styleOpts.map((s) => (
          <ColumnItem
            key={s}
            to={`/collections/all?style=${encodeURIComponent(s)}`}
            label={s}
            testId={`flyout-shop-mat-${slugify(s)}`}
            onNavigate={onNavigate}
          />
        ))}
      </ul>
      <div className="mt-6 pt-5 border-t border-[#C9B596]">
        <ul>
          <ColumnItem
            to="/collections/all"
            label="Shop all"
            testId="flyout-shop-all"
            isFooter
            onNavigate={onNavigate}
          />
        </ul>
      </div>
    </div>
  </div>
);

const MainContent = ({ collection, sub, onNavigate }) => {
  const slugMain = slugify(collection);
  const subs = sub[collection] || [];
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-16">
      <div>
        <ColumnHeader
          to={`/collections/${slugMain}`}
          testId={`flyout-${slugMain}-heading`}
          onNavigate={onNavigate}
        >
          {collection}
        </ColumnHeader>
        <p className="text-[14px] leading-[1.7] text-[#5B3A24] max-w-xs mb-6">
          Browse every {collection.toLowerCase()} piece — curated, made on order in Australia.
        </p>
        <Link
          to={`/collections/${slugMain}`}
          onClick={onNavigate}
          data-testid={`flyout-${slugMain}-view-all`}
          className="inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase text-[#33200F] border-b border-[#33200F] pb-1 hover:gap-3 transition-all"
        >
          View all {collection}
          <ArrowRight className="h-3 w-3" />
        </Link>
      </div>

      <div className="md:col-span-2">
        <ColumnHeader
          to={`/collections/${slugMain}`}
          testId={`flyout-${slugMain}-subs-heading`}
          onNavigate={onNavigate}
        >
          By category
        </ColumnHeader>
        {subs.length === 0 ? (
          <p className="text-[13px] italic text-[#A07A4F]">No sub-collections yet.</p>
        ) : (
          <ul className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-0.5">
            {subs.map((s) => (
              <ColumnItem
                key={s}
                to={`/collections/${slugMain}/${slugify(s)}`}
                label={s}
                testId={`flyout-${slugMain}-${slugify(s)}`}
                onNavigate={onNavigate}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

// ----- Parent component -----

const NavFlyouts = ({ tone = 'dark' }) => {
  const { main, sub, style: styleOpts } = useTaxonomy();
  const [openMain, setOpenMain] = useState(null);
  const [panelTop, setPanelTop] = useState(0);
  const navRef = useRef(null);
  const closeTimer = useRef(null);

  useEffect(() => {
    const update = () => {
      if (!navRef.current) return;
      const rect = navRef.current.getBoundingClientRect();
      setPanelTop(rect.bottom);
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  const open = (key) => {
    clearTimeout(closeTimer.current);
    setOpenMain(key);
  };
  const scheduleClose = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenMain(null), 180);
  };
  const cancelClose = () => clearTimeout(closeTimer.current);
  const closeNow = () => setOpenMain(null);
  useEffect(() => () => clearTimeout(closeTimer.current), []);

  // All sub-collections deduped (for the Shop mega panel).
  const allCategories = [];
  const seenCat = new Set();
  main.forEach((m) => {
    (sub[m] || []).forEach((s) => {
      if (!seenCat.has(s)) { seenCat.add(s); allCategories.push({ main: m, sub: s }); }
    });
  });

  const navLinkColor = tone === 'light' ? 'text-[#F5EDDD]' : 'text-[#33200F]';
  const navHoverColor = tone === 'light' ? 'hover:text-[#E3D5B8]' : 'hover:text-[#A07A4F]';
  const linkBase =
    `inline-flex items-center gap-1 text-[12px] xl:text-[13px] tracking-[0.14em] xl:tracking-[0.16em] uppercase ${navLinkColor} ${navHoverColor} transition-colors`;

  const isOpen = openMain !== null;
  const isShop = openMain === '__shop';
  const isMain = openMain && main.includes(openMain);

  return (
    <>
      <nav
        ref={navRef}
        className="hidden lg:flex items-center gap-5 xl:gap-7 mx-auto pl-10"
        onMouseLeave={scheduleClose}
        data-testid="nav-flyouts"
      >
        <NavLink
          to="/"
          end
          onMouseEnter={closeNow}
          className={({ isActive }) =>
            `${linkBase} ${isActive ? 'relative after:absolute after:left-0 after:right-0 after:-bottom-1.5 after:h-px after:bg-[#5B3A24]' : ''}`
          }
        >
          Home
        </NavLink>

        <div onMouseEnter={() => open('__shop')}>
          <NavLink to="/collections" data-testid="nav-shop" className={linkBase}>
            Shop <ChevronDown className="h-3 w-3" strokeWidth={1.5} />
          </NavLink>
        </div>

        {main.map((m) => {
          const slugMain = slugify(m);
          return (
            <div key={m} onMouseEnter={() => open(m)}>
              <NavLink
                to={`/collections/${slugMain}`}
                data-testid={`nav-${slugMain}`}
                className={({ isActive }) =>
                  `${linkBase} ${isActive ? 'relative after:absolute after:left-0 after:right-0 after:-bottom-1.5 after:h-px after:bg-[#5B3A24]' : ''}`
                }
              >
                {m} <ChevronDown className="h-3 w-3" strokeWidth={1.5} />
              </NavLink>
            </div>
          );
        })}

        <NavLink to="/about" className={linkBase} onMouseEnter={closeNow}>About Us</NavLink>
        <NavLink to="/contact" className={linkBase} onMouseEnter={closeNow}>Contact</NavLink>
      </nav>

      {/* Single persistent panel — content swaps without remount. */}
      {isOpen && (
        <>
          {/* Transparent hover-bridge spanning the gap between nav and panel. */}
          <div
            className="fixed left-0 right-0 z-30"
            style={{ top: panelTop - 14, height: 14 }}
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
            aria-hidden
          />
          <div
            className="fixed left-0 right-0 z-30"
            style={{
              top: panelTop,
              backgroundColor: PANEL_BG,
              borderTop: `1px solid ${PANEL_BORDER}`,
              borderBottom: `1px solid ${PANEL_BORDER}`,
              boxShadow: '0 30px 50px -30px rgba(91,58,36,0.35)',
            }}
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
            data-testid={isShop ? 'flyout-panel-shop' : `flyout-panel-${slugify(openMain || '')}`}
          >
            <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-10 min-h-[280px]">
              {isShop && (
                <ShopContent
                  main={main}
                  sub={sub}
                  styleOpts={styleOpts}
                  allCategories={allCategories}
                  onNavigate={closeNow}
                />
              )}
              {isMain && (
                <MainContent collection={openMain} sub={sub} onNavigate={closeNow} />
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default NavFlyouts;
